import { CSSInterpolation } from '@emotion/css/create-instance'
import { useCombobox, type UseComboboxStateChange } from 'downshift'
import React from 'react'
import Measure from 'react-measure'

import type { RequireAtLeastOne } from '../../../types'
import { joinOptionalStringsToSpaceSeparatedString } from '../../../util/joinOptionalStringsToSpaceSeparatedString'
import { CssVars } from '../../themes'
import { AnchoredOverlay } from '../AnchoredOverlay'
import { FoundationIcon } from '../FoundationIcon'
import {
  isMenuOptionSelectable,
  MenuOption,
  MenuOptionsList,
  MenuOptionsListError,
  type MenuOptionsListType,
  type MenuOptionType,
  type MenuOptionVariantSelectable,
  useFlattenedAndFilteredMenuOptions,
} from '../MenuOption'
import { Type } from '../Type'
import { useUniqueId } from '../useUniqueId'
import { ErrorMessageManager } from './ErrorMessageManager'
import { InternalInput } from './InternalInput'

export type BaseProps<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
> = {
  /**
   * The space-separated list of element IDs that describe the component.
   * Can be used to associate this field with error messages shown outside
   * this component.
   */
  'aria-describedby'?: string

  /**
   * An error message to show below the input.
   * Will only be shown if `state` is `"error"`.
   */
  errorMessage?: string

  /**
   * Placeholder text to show when the input is empty.
   */
  inputPlaceholder?: React.ComponentProps<typeof InternalInput>['placeholder']

  /**
   * A React ref to attach to the actual `input` element,
   * e.g. for triggering focus or blur on the input.
   */
  inputRef?: React.Ref<HTMLInputElement>

  /**
   * Callback for when the input losses focus.
   */
  onInputBlur?: React.ComponentProps<typeof InternalInput>['onBlur']

  /**
   * Callback for when the value in the input changes.
   */
  onInputChange: (value: string | undefined) => void

  /**
   * Optional callback for when the input gains focus.
   */
  onInputFocus?: React.ComponentProps<typeof InternalInput>['onFocus']

  /**
   * Optional callback for when the menu opens.
   */
  onMenuOpen?: () => void

  /**
   * Optional callback for when the menu closes.
   */
  onMenuClose?: () => void

  /**
   * Callback for when an option has been selected.
   */
  onSelect: (item: TOption | null | undefined) => void

  /**
   * List of menu options to be displayed.
   */
  options: MenuOptionsListType<TOption>

  /**
   * Pass a CSS object if you need to override styles on the root element.
   * Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation

  /**
   * Whether to disable the input or mark it as having an error.
   */
  state?: 'default' | 'disabled' | 'error'

  /**
   * Optional, designated a selected option from the list of options provided.
   */
  selectedOptionValue?: TOption['value']

  /**
   * The value to show in the input. If empty, any provided `inputPlaceholder`
   * is shown.
   */
  value: string

  /**
   * Determines the style of the component.
   */
  variant?: 'default' | 'search'
}

export type LabelProps = RequireAtLeastOne<{
  /**
   * A human readable label for the component.
   */
  'aria-label'?: string

  /**
   * The space-separated list of element IDs that provides the label for the
   * component.
   */
  'aria-labelledby'?: string

  /**
   * Optional identifier for the input.
   */
  inputId?: React.ComponentProps<typeof InternalInput>['id']

  /**
   * A label to show above the input. If you do not provide a value here, please make sure to
   * provide your own `<label>` element for the input for accessibility reasons.
   */
  label?: string
}>

export function ComboBox<
  TOption extends MenuOptionVariantSelectable = MenuOptionVariantSelectable
>({
  'aria-describedby': externalAriaDescribedBy,
  'aria-label': externalAriaLabel,
  'aria-labelledby': externalAriaLabelledBy,
  errorMessage,
  inputId: externalInputId,
  inputPlaceholder,
  inputRef: externalInputRef,
  label = '',
  onInputBlur,
  onInputChange,
  onInputFocus,
  onMenuClose,
  onMenuOpen,
  onSelect,
  options,
  overrideRootStyles,
  state = 'default',
  value,
  variant = 'default',
}: BaseProps<TOption> & LabelProps) {
  const flattenedOptions = useFlattenedAndFilteredMenuOptions<TOption>(options)

  // NOTE(yan): downshift's built in id system is sequential. when there's
  //   multiple instances of the design system being used on a page, the ids
  //   might collide. so we prefix with an uuid to ensure global uniqueness.
  const comboBoxInstanceId = useUniqueId()
  const labelId = label ? `${comboBoxInstanceId}-label` : undefined
  const menuId = `${comboBoxInstanceId}-menu`
  const inputId = externalInputId || `${comboBoxInstanceId}-input`

  const handleInputValueChange = React.useCallback(
    (changes: UseComboboxStateChange<MenuOptionType<TOption>>) => {
      if (
        changes.type === useCombobox.stateChangeTypes.InputKeyDownEnter ||
        changes.type === useCombobox.stateChangeTypes.ItemClick
      ) {
        // NOTE(yan): this is a controlled input component, prevent changing
        //   input value automatically when item is selected
        return
      }
      onInputChange(changes.inputValue ?? '')
    },
    [onInputChange]
  )

  const handleSelectedItemChange = React.useCallback(
    (changes: UseComboboxStateChange<MenuOptionType<TOption>>) => {
      if (isMenuOptionSelectable(changes.selectedItem)) {
        onSelect(changes.selectedItem)
      } else if (changes.selectedItem?.variant === 'button') {
        changes.selectedItem.onClick()
      }
    },
    [onSelect]
  )

  const handleOpenChange = React.useCallback(
    (changes: UseComboboxStateChange<MenuOptionType<TOption>>) => {
      if (changes.isOpen && onMenuOpen) {
        onMenuOpen()
      } else if (!changes.isOpen && onMenuClose) {
        onMenuClose()
      }
    },
    [onMenuClose, onMenuOpen]
  )

  const handleGetOptionElementIdentifier = React.useCallback(
    (option: TOption) => {
      return `${comboBoxInstanceId}-options-menu-list-option-${option.value}`
    },
    [comboBoxInstanceId]
  )

  const handleGetOptionElementIdentifierByIndex = React.useCallback(
    (index: number) => {
      const option = flattenedOptions[index]
      if (isMenuOptionSelectable(option)) {
        return handleGetOptionElementIdentifier(option)
      }
      return `${comboBoxInstanceId}-options-menu-list-option-${index}`
    },
    [comboBoxInstanceId, flattenedOptions, handleGetOptionElementIdentifier]
  )

  const {
    getComboboxProps,
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    isOpen,
    openMenu,
  } = useCombobox({
    getItemId: handleGetOptionElementIdentifierByIndex,
    inputId,
    inputValue: value,
    itemToString: o => o?.label || '',
    items: flattenedOptions,
    labelId,
    menuId,
    onInputValueChange: handleInputValueChange,
    onIsOpenChange: handleOpenChange,
    onSelectedItemChange: handleSelectedItemChange,
  })

  const inputRef = React.useRef<HTMLInputElement | null>(null)
  // TODO(yan): consolidate this logic with other instances (PhoneNumberInput, SSNInput, SelectMenu)
  const setInputRef = React.useCallback(
    (element: HTMLInputElement | null) => {
      inputRef.current = element
      if (typeof externalInputRef === 'function') {
        externalInputRef(element)
      } else if (externalInputRef) {
        ;(
          externalInputRef as React.MutableRefObject<HTMLInputElement | null>
        ).current = element
      }
    },
    [externalInputRef]
  )

  // NOTE(yan):
  // between WAI-ARIA 1.2 and 1.1 the combobox spec had a breaking change. in
  //   1.2 it's an anti-pattern to a container element with role="combobox".
  //   instead it is now applied directly on the input element. see links below
  //   for examples.
  //   https://www.w3.org/TR/wai-aria-practices-1.1/examples/combobox/aria1.1pattern/listbox-combo.html
  //   https://www.w3.org/TR/wai-aria-practices-1.2/examples/combobox/combobox-autocomplete-list.html
  // downshift builts the comboxbox to 1.1 specs because 1.2 is in draft.
  //   however, VoiceOver no longer supports 1.1 version so comboboxes on macOS
  //   (regardless of browser) need to be 1.2 for it be functional with VO.
  // until downshift supports 1.2 by default, hack the combobox props to be 1.2
  //   compliant. the upgrade issue with downshift is tracked here:
  //   https://github.com/downshift-js/downshift/issues/1239
  const { ref: setDownshiftComboboxRef, ...comboboxProps } = getComboboxProps()

  // NOTE(yan): measure the whole combo box to anchor the menu list
  const internalComboBoxRef = React.useRef<HTMLDivElement | null>(null)
  const setComboBoxRef = React.useCallback(
    (
      element: HTMLDivElement | null,
      ...additionalSetters: Array<(ref: Element | null) => void>
    ) => {
      internalComboBoxRef.current = element
      if (typeof setDownshiftComboboxRef === 'function') {
        setDownshiftComboboxRef(element)
      } else if (setDownshiftComboboxRef) {
        ;(
          setDownshiftComboboxRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = element
      }
      additionalSetters.forEach(setter => {
        setter(element)
      })
    },
    [setDownshiftComboboxRef]
  )

  const { ref: menuRef, ...menuProps } = getMenuProps({
    'aria-labelledby': joinOptionalStringsToSpaceSeparatedString(
      labelId,
      externalAriaLabelledBy
    ),
  })

  // NOTE(yan): We keep track of the width of the toggle button so that we can
  //  set the size of the menu, which is rendered in a portal, to match
  const [inputWidth, setInputWidth] = React.useState(0)

  return (
    <ErrorMessageManager
      errorMessage={state === 'error' ? errorMessage : undefined}
    >
      {(errorMessageId, errorMessageElem, errorMessageString) => (
        <div css={overrideRootStyles}>
          {label && (
            <label
              css={{
                display: 'block',
                marginBottom: CssVars.size.gutterWidthSm,
                width: '100%',
              }}
              {...getLabelProps()}
            >
              <Type.Text size="S">{label}</Type.Text>
            </label>
          )}
          <Measure
            bounds
            innerRef={setComboBoxRef} // will be set below through measureRef
            onResize={contentRect => {
              setInputWidth(contentRect.bounds?.width ?? 0)
            }}
          >
            {({ measureRef }: { measureRef: React.LegacyRef<HTMLDivElement> }) => {
              const {
                onChange: onChangeFromGetInputProps,
                ref,
                ...inputProps
              } = getInputProps({
                'aria-controls': menuId,
                'aria-describedby': joinOptionalStringsToSpaceSeparatedString(
                  errorMessageId,
                  externalAriaDescribedBy
                ),
                'aria-label': externalAriaLabel,
                'aria-labelledby': externalAriaLabelledBy,
                onBlur: onInputBlur,
                onFocus: e => {
                  onInputFocus?.(e)
                  if (options.length && !isOpen) {
                    openMenu()
                  }
                },
                placeholder: inputPlaceholder,
                ref: setInputRef,
                type: 'text',
              })
              return (
                <div ref={measureRef}>
                  <InternalInput
                    {...inputProps}
                    {...comboboxProps}
                    innerRef={ref}
                    onChange={({ event }) => onChangeFromGetInputProps(event)}
                    sizes={
                      variant === 'search' ? { inputMinWidth: 400 } : undefined
                    }
                    startControl={
                      variant === 'search' && (
                        <div
                          css={{
                            alignItems: 'center',
                            display: 'flex',
                            marginRight: 8,
                          }}
                        >
                          <FoundationIcon type="Search16" />
                        </div>
                      )
                    }
                    state={
                      variant === 'search' && state === 'error'
                        ? 'default'
                        : state
                    }
                  />
                </div>
              )
            }}
          </Measure>
          {variant === 'default' && errorMessageElem}
          {variant === 'search' && errorMessageString && (
            <AnchoredOverlay
              dangerouslyEnforceZIndex={
                CssVars.size._experimentalSelectMenuZIndex
              }
              gap={4}
              isOpen={isOpen}
              targetEl={internalComboBoxRef.current}
            >
              <MenuOptionsListError
                errorMessageId={errorMessageId}
                innerRef={menuRef}
                isMenuOpen={isOpen}
                minWidth={inputWidth}
                variant={variant}
                width={inputWidth}
                {...menuProps}
              >
                {errorMessageString}
              </MenuOptionsListError>
            </AnchoredOverlay>
          )}
          <AnchoredOverlay
            dangerouslyEnforceZIndex={
              CssVars.size._experimentalSelectMenuZIndex
            }
            gap={4}
            isOpen={
              isOpen &&
              options.length > 0 &&
              !(variant === 'search' && errorMessageString)
            }
            isPersistent
            targetEl={internalComboBoxRef.current}
          >
            <MenuOptionsList<TOption>
              innerRef={menuRef}
              isMenuOpen={isOpen}
              minWidth={inputWidth}
              optionsList={options}
              variant={variant}
              width={inputWidth}
              {...menuProps}
            >
              {(option, optionIndex) => {
                const optionValue: TOption['value'] | undefined =
                  isMenuOptionSelectable(option) ? option.value : undefined
                const { ref, ...itemProps } = getItemProps({
                  disabled:
                    isMenuOptionSelectable(option) &&
                    option.state === 'disabled',
                  index: optionIndex,
                  item: option,
                })
                return (
                  <MenuOption<TOption>
                    // HACK(eric.lovett): If the items in this menu have integer values, then
                    // it's possible for one of them to have the same value as the index of a
                    // button entry, which results in a React key collision. To reduce the risk
                    // of collisions, we add a prefix to the key that is very unlikely to
                    // collide with real usage values.
                    key={optionValue || `--Bento-Web-key-${optionIndex}`}
                    index={optionIndex}
                    innerRef={ref}
                    isHighlighted={optionIndex === highlightedIndex}
                    option={option}
                    substring={value}
                    variant={variant}
                    {...itemProps}
                  />
                )
              }}
            </MenuOptionsList>
          </AnchoredOverlay>
        </div>
      )}
    </ErrorMessageManager>
  )
}
